import { getMonthIndexByDayIndex } from "@/utils/getMonthIndexByDayIndex";

interface MonthMeta {
  startIndex: number;
  days: number;
}

interface UseCalendarScrollProps {
  today: Date;
  days: Date[];
  months: MonthMeta[];
  scrollableAreaRef: React.RefObject<HTMLDivElement>;
  monthHeaderRef: React.RefObject<HTMLDivElement>;
  cellWidth: number;
}

export const useCalendarScroll = ({
  days,
  scrollableAreaRef,
  monthHeaderRef,
  cellWidth,
  today,
  months,
}: UseCalendarScrollProps) => {
  const scrollMonthHeaderToMonthIndex = (
    monthIndex: number,
    behavior: ScrollBehavior = "instant",
  ) => {
    if (!monthHeaderRef.current) return;
    const scrollLeft = 150 * monthIndex;
    monthHeaderRef.current.scrollTo({ left: scrollLeft, behavior });
  };

  const scrollToDay = (
    targetDate: Date,
    behavior: ScrollBehavior = "instant",
    offsetDays: number = 0,
    syncMonthHeader = true,
  ) => {
    if (!scrollableAreaRef.current) return;

    const index = days.findIndex(
      (day) => day.toDateString() === targetDate.toDateString(),
    );
    if (index === -1) return;

    const scrollLeft = cellWidth * (index + offsetDays);
    scrollableAreaRef.current.scrollTo({ left: scrollLeft, behavior });

    if (syncMonthHeader) {
      const monthIndex = getMonthIndexByDayIndex(index + offsetDays, months);
      if (monthIndex !== -1) {
        scrollMonthHeaderToMonthIndex(monthIndex, behavior);
      }
    }

    return index;
  };

  const scrollToToday = (behavior: ScrollBehavior = "instant") => {
    scrollToDay(today, behavior, -3);
  };

  const getFirstVisibleDayIndex = () => {
    if (!scrollableAreaRef.current) return 0;
    return Math.floor(scrollableAreaRef.current.scrollLeft / cellWidth);
  };

  const scrollToPreviousMonth = () => {
    const firstVisibleIndex = getFirstVisibleDayIndex();
    const currentMonthIndex = getMonthIndexByDayIndex(
      firstVisibleIndex,
      months,
    );
    if (currentMonthIndex <= 0) return;
    const prevMonth = months[currentMonthIndex - 1];
    if (prevMonth) {
      scrollToDay(days[prevMonth.startIndex], "instant");
    }
  };

  const scrollToNextMonth = () => {
    const firstVisibleIndex = getFirstVisibleDayIndex();
    const currentMonthIndex = getMonthIndexByDayIndex(
      firstVisibleIndex,
      months,
    );
    if (currentMonthIndex === -1 || currentMonthIndex >= months.length - 1)
      return;
    const nextMonth = months[currentMonthIndex + 1];
    if (nextMonth) {
      scrollToDay(days[nextMonth.startIndex], "instant");
    }
  };

  const scrollToPreviousWeek = () => {
    const firstVisibleIndex = getFirstVisibleDayIndex();
    const targetIndex = Math.max(0, firstVisibleIndex - 3);
    if (days[targetIndex]) {
      scrollToDay(days[targetIndex], "instant", 0);
    }
  };

  const scrollToNextWeek = () => {
    const firstVisibleIndex = getFirstVisibleDayIndex();
    const targetIndex = Math.min(days.length - 1, firstVisibleIndex + 3);
    if (days[targetIndex]) {
      scrollToDay(days[targetIndex], "instant", 0);
    }
  };

  return {
    scrollToDay,
    scrollToToday,
    scrollToPreviousMonth,
    scrollToNextMonth,
    scrollToPreviousWeek,
    scrollToNextWeek,
    getFirstVisibleDayIndex,
  };
};
